/**
 * Sign-in limit wording, shared by the API (the 429 answers) and the admin sign-in page (docs/sign-in.md, "Sign-in
 * limits"). Ported from the Hype Forge Base, 2026-10-22. Pure and imported on its own as `@vv/sections/sign-in`, so the
 * API does not pull in the section schemas: the numbers themselves live in the API (`SIGN_IN_LIMITS` in
 * apps/api/src/ratelimit.ts); this only turns a Retry-After into words.
 */

/** "45 seconds", "1 minute", "12 minutes", "3 hours": a Retry-After (seconds) rounded UP, so nobody is told too early. */
export function signInWaitText(seconds: number): string {
  const s = Number.isFinite(seconds) ? Math.max(1, Math.ceil(seconds)) : 60;
  if (s < 60) return s === 1 ? '1 second' : `${s} seconds`;
  if (s < 90 * 60) {
    const minutes = Math.ceil(s / 60);
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  }
  const hours = Math.ceil(s / 3600);
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

/** The whole sentence a limited sign-in shows: "Too many sign-in attempts. Try again in 12 minutes." */
export function signInWaitMessage(seconds: number): string {
  return `Too many sign-in attempts. Try again in ${signInWaitText(seconds)}.`;
}

/** Seconds out of a 429 answer: its JSON `retry_after`, else the Retry-After header, else a minute. */
export function retryAfterFrom(body: unknown, header: string | null | undefined): number {
  const fromBody = body && typeof body === 'object' ? Number((body as { retry_after?: unknown }).retry_after) : NaN;
  if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);
  const fromHeader = Number(header);
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader);
  return 60;
}
